import {
  AudioOutlined,
  ClockCircleOutlined,
  PlusOutlined,
  SettingOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import { Button, Col, Divider, Row, Typography, message } from "antd";
import dayjs from "dayjs";
import React from "react";
import { useDispatch } from "react-redux";
import Swal from "sweetalert2";
import { isPracticeAdminOnly } from "../../domain/user.domain";
import { useAppSelector } from "../../hooks/redux-hooks";
import useSpecialityReference from "../../hooks/useSpecialityReference";
import type { ScheduleSlot } from "../../service/schedulePick.service";
import {
  setAITemplateModalState,
  setCreateModalIntent,
  setCreateModalState,
  setEditingState,
  setShowRestrictionModal,
  setShowSettingsModal,
} from "../../slices/appStateSlice";
import { setRecordingDetail } from "../../slices/recordingsSlice";
import { isSafariDesktop } from "../../utils/device.utils";
import { eventEmitter } from "../../utils/eventEmitter";
import { type QuickCapturePreset, rememberTemplateChoice } from "../../utils/sessionTemplate";
import { minutesSince } from "../CreateNoteModal/schedulePick.utils";
import type { RecordCTAProps } from "./ClassicRecordCTA.component";
import MicTestModal from "./MicTestModal.component";
import QuickStartCard, { SeeAll, TemplateLine } from "./QuickStartCard.component";
import RecordFirstPopover from "./RecordFirstPopover.component";
import { firstNameOf, slotTimingLabel } from "./quickStart";
import styles from "./quickstart.module.scss";
import { type QuickStartSchedule, useQuickStartSchedule, useSessionTemplate } from "./useQuickStart";

/**
 * One muted line under Start Encounter, only when today's schedule is done. A schedule that
 * hasn't synced or has no appointments says nothing: most practices have no calendar sync,
 * and the line would only confuse them.
 */
const scheduleNote = (schedule: QuickStartSchedule): string | null => {
  const pick = schedule.pick;
  if (!schedule.enabled || !schedule.hasEhrSchedule || !pick || schedule.view.kind !== "none") return null;
  if (pick.state === "ready" && pick.documented.length > 0) {
    return `All ${pick.schedule_size} of today's appointments are documented.`;
  }
  return null;
};

/**
 * The sidebar's Start card with Quick Start (behind the QUICK_START_* Constance flags):
 * the predicted patient and template, one click to record. When there is no confident
 * pick, or the clinician turned it down, it is the Start Encounter card, which works
 * exactly as the classic one does.
 */
const QuickStartCTA = ({ captureRef }: RecordCTAProps) => {
  const dispatch = useDispatch();

  const { uploadInProgress, selectedClient, isMobilePath, isDemoPath } = useAppSelector((state) => state.appState);
  const { recordingDetail, isRecording, isPaused } = useAppSelector((state) => state.recordings);
  const userInfo = useAppSelector((state) => state.auth.userInfo);
  const { sessionTerm } = useSpecialityReference();
  const [micTestOpen, setMicTestOpen] = React.useState(false);
  const [startingId, setStartingId] = React.useState<number | null>(null);
  const schedule = useQuickStartSchedule();
  const session = useSessionTemplate();
  const { view } = schedule;

  // Safari and an unfinished upload block every way of starting.
  const canStart = () => {
    //check if it is safari desktop browser if yes we need to show a modal to the user to ask them to use the chrome version or microsoft edge version
    if (isSafariDesktop()) {
      Swal.fire({
        title: "Browser Compatibility",
        text: "Please use Chrome or Microsoft Edge browsers for the best experience. Nudge can no longer support Safari due to browser limitations.",
        icon: "info",
        confirmButtonText: "Got it",
      });
      return false;
    }
    if (uploadInProgress) {
      message.warning("Please wait for the current upload to finish.");
      return false;
    }
    return true;
  };

  // Start Encounter: the classic start (the drawer, or record-first's one-click capture). After
  // a × the drawer must not pre-select the very patient the clinician just turned down.
  const startEncounter = () => {
    if (!canStart()) {
      return;
    }
    if (userInfo.record_first && !isDemoPath) {
      eventEmitter.emit("quickRecord");
      return;
    }
    dispatch(
      setRecordingDetail({
        ...recordingDetail,
        modality_type: "audio",
        client_id: selectedClient?.id,
      }),
    );
    if (view.kind === "dismissed") dispatch(setCreateModalIntent("manual"));
    dispatch(setEditingState(false));
    dispatch(setCreateModalState(true));
  };

  // Quick Start: that patient with the card's template, recording on one click; Submit then
  // processes it directly (or, with no template on file, opens the details drawer to choose one).
  const quickStart = (slot: ScheduleSlot) => {
    if (slot.client.id == null || startingId !== null || !canStart()) {
      return;
    }
    const template = session.template;
    const suggestion = schedule.pick?.suggestion;
    const preset: QuickCapturePreset = {
      client: { id: slot.client.id, uuid: slot.client.uuid ?? null, name: slot.client.name },
      template,
      startedEvent: {
        view: view.kind,
        chosen_from_see_all: view.kind === "quick" && view.chosen,
        candidates: view.kind === "choose" ? view.slots.length + view.more : 1,
        was_suggestion: suggestion?.client_id === slot.client.id,
        pick_reason: suggestion?.reason ?? null,
        confidence: suggestion?.confidence ?? null,
        rank: schedule.upcoming.indexOf(slot) + 1,
        slot_offset_min: minutesSince(slot.scheduled_at),
        has_template: template !== null,
        template_source: template?.source ?? null,
      },
      onSettled: () => setStartingId(null),
    };
    // A Quick Start is a use of the template, exactly like picking it in the drawer:
    // remember it so the drawer and the next card start from it on this device.
    if (template) rememberTemplateChoice(template.value, template.isTemplate);
    setStartingId(slot.client.id);
    eventEmitter.emit("quickRecord", preset);
  };

  const recordWithoutPatient = () => {
    if (!canStart()) {
      return;
    }
    eventEmitter.emit("quickRecord", { unassigned: true } satisfies QuickCapturePreset);
  };

  const openAITemplateModal = () => {
    if (isMobilePath) {
      dispatch(setShowRestrictionModal(true));
    } else {
      dispatch(setAITemplateModalState(true));
    }
  };

  if (isPracticeAdminOnly(userInfo)) {
    return null;
  }

  const footer = (
    <div className={styles.Card__footer}>
      <button
        type="button"
        className={styles.Card__footerAction}
        onClick={() => setMicTestOpen(true)}
        disabled={isRecording || isPaused}
      >
        <AudioOutlined style={{ fontSize: 14 }} />
        Test Mic
      </button>
      <Divider type="vertical" style={{ margin: 0, height: 16 }} />
      <button type="button" className={styles.Card__footerAction} onClick={() => dispatch(setShowSettingsModal(true))}>
        <SettingOutlined style={{ fontSize: 14 }} />
        Settings
      </button>
    </div>
  );

  const note = scheduleNote(schedule);

  return (
    <>
      <Row gutter={[10, 10]} ref={captureRef} className={styles.Panel}>
        <Col span={24} className={styles.Panel__header}>
          <Typography className={styles.Panel__title}>Generate Note</Typography>
        </Col>
        <Col span={24}>
          {view.kind === "quick" || view.kind === "choose" ? (
            <QuickStartCard
              schedule={schedule}
              session={session}
              startingId={startingId}
              onQuickStart={quickStart}
              onRecordWithoutPatient={recordWithoutPatient}
              footer={footer}
            />
          ) : (
            <div className={styles.Card} data-testid="start-encounter-card">
              <Button
                type="primary"
                block
                icon={<PlusOutlined />}
                className={styles.Card__primary}
                style={{ marginTop: 0 }}
                onClick={startEncounter}
                data-testid="start-encounter"
              >
                Start {sessionTerm}
              </Button>
              <Button
                block
                className={styles.Card__secondary}
                icon={
                  <svg width="16" height="16" viewBox="9 9 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M12.098 27.902a3.752 3.752 0 0 0 2.652 1.099m-2.652-1.099a3.751 3.751 0 0 0 2.652 1.099m-2.652-1.099A3.75 3.75 0 0 1 11 25.25V12.125c0-.621.504-1.125 1.125-1.125h5.25c.621 0 1.125.504 1.125 1.125v4.072m-3.75 12.804a3.75 3.75 0 0 0 2.652-1.099m-2.652 1.099c.995 0 1.949-.396 2.652-1.099m-2.652 1.099L27.875 29c.621 0 1.125-.504 1.125-1.125v-5.25c0-.621-.504-1.125-1.125-1.125h-4.072m-6.401 6.402 6.401-6.402m-6.401 6.402A3.75 3.75 0 0 0 18.5 25.25v-9.053m5.303 5.303 2.879-2.88c.44-.438.44-1.15 0-1.59l-3.712-3.713a1.124 1.124 0 0 0-1.59 0l-2.88 2.88m-3.75 9.053h.008v.008h-.008v-.008Z"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                }
                onClick={openAITemplateModal}
              >
                My templates
              </Button>
              {view.kind === "later" && (
                <div className={styles.Next} data-testid="quick-start-later">
                  <div className={styles.Next__top}>
                    <span className={styles.Next__when}>
                      <ClockCircleOutlined />{" "}
                      {/* A slot already past (nobody seen yet, running far behind) isn't "up next". */}
                      {dayjs(view.slot.scheduled_at).isAfter(dayjs()) ? "Up next" : "Not seen yet"} ·{" "}
                      {slotTimingLabel(view.slot.scheduled_at, dayjs())}
                    </span>
                    <SeeAll schedule={schedule} currentId={null} onRecordWithoutPatient={recordWithoutPatient} />
                  </div>
                  <div className={styles.Next__name}>{view.slot.client.name}</div>
                  <TemplateLine session={session} />
                  <Button
                    size="small"
                    block
                    icon={<ThunderboltOutlined />}
                    loading={startingId !== null || !session.ready}
                    className={styles.Next__start}
                    onClick={() => quickStart(view.slot)}
                    data-testid="quick-start-later-start"
                  >
                    {["Quick Start", firstNameOf(view.slot.client.name)].filter(Boolean).join(" · ")}
                  </Button>
                </div>
              )}
              {view.kind === "dismissed" && (
                <div className={styles.Card__note} data-testid="quick-start-dismissed">
                  Quick Start is off for {view.slot.client.name}.{" "}
                  <Typography.Link style={{ fontSize: 12 }} onClick={schedule.restore}>
                    Undo
                  </Typography.Link>
                </div>
              )}
              {note && (
                <div className={styles.Card__note} data-testid="schedule-note">
                  {note}
                </div>
              )}
              {footer}
            </div>
          )}
        </Col>
        <Col span={24}>
          <RecordFirstPopover />
        </Col>
      </Row>
      <MicTestModal open={micTestOpen} onClose={() => setMicTestOpen(false)} />
    </>
  );
};

export default QuickStartCTA;
